/* eslint-disable @typescript-eslint/no-unused-vars */
import type { FormData as ProductFormData, PropertyData } from '../types'
import { serializeProductMedia } from './serializeMedia'
import { buildSaleModePayload } from './saleMode'
import { buildTaxonomyPayload } from './taxonomy'
import {
  optionalIdField,
  requiredId,
} from '../../../features/products/utils/productPayload'
import { parseStockUpTrigger } from '../../../features/products/utils/stockStatus'

interface ApiData {
  categoriesData?: { data?: Array<{ id: string; name: string }> }
  colorsData?: { data?: Array<{ id: string; name: string; hexValue: string }> }
  materialsData?: { data?: Array<{ id: string; name: string }> }
  sizeGuideData?: unknown
}

const generateUniqueId = () => {
  return Math.random().toString(36).substr(2, 9)
}

export const transformToApiFormat = (
  formData: ProductFormData, 
  propertyData: PropertyData, 
  apiData: ApiData, 
  status: 'DRAFT' | 'PUBLISHED'
): FormData => {
  const { colorsData, materialsData } = apiData
  const multipartFormData = new FormData()
  // PE-2169 — one product-level threshold, stamped onto every leaf.
  const stockUpTrigger = parseStockUpTrigger(formData.stockUpTrigger)
  const imageNames: string[] = []

  const sizes = propertyData.sizes.map((sizeData) => ({
    sizeId: sizeData.sizeId,
    additionalPrice: sizeData.price,
    additionalCostPrice: sizeData.additionalCostPrice,
    materials: sizeData.materials.map((materialData) => ({
      materialId: requiredId(
        materialsData?.data?.find(
          (m: { name: string }) => m.name === materialData.name
        )?.id,
        'material',
        materialData.name,
      ),
      additionalPrice: materialData.price,
      additionalCostPrice: materialData.additionalCostPrice,
      colours: materialData.colors.map((colorData) => {
        const colorName = colorsData?.data?.find(
          (c: { hexValue: string }) => c.hexValue === colorData.hex
        )?.name || 'unknown'
        
        // Generate unique names for images and add to FormData
        const colorImageNames = colorData.images.map((image, _index) => {
          const uniqueId = generateUniqueId()
          const imageName = `color-${colorName}-${uniqueId}`
          multipartFormData.append('files', image, imageName)
          return imageName
        })
        imageNames.push(...colorImageNames);

        return {
          colourId: requiredId(
            colorsData?.data?.find(
              (c: { hexValue: string }) => c.hexValue === colorData.hex
            )?.id,
            'colour',
            colorData.hex,
          ),
          additionalPrice: colorData.price,
          additionalCostPrice: colorData.additionalCostPrice,
          quantity: colorData.quantity,
          weight: colorData.weight,
          imageUrls: colorImageNames,
          stockUpTrigger,
        }
      }),
    })),
  }));

  // PE-1283 §7 E4 — product-level media (images + transcoded video). Only emit the
  // key when there is at least one persisted asset so image-only products are byte-
  // for-byte identical to before (backward-compatible).
  const media = serializeProductMedia(formData.media)

  // PE-2087 — many-to-many taxonomy links. ⚠️ TWO NAMESPACES: `categoryId` below is
  // rack-api's OWN legacy Category table (required scalar, resolved by
  // prisma.category.findFirst); `collectionIds`/`categoryIds`/`occasionIds` are
  // product-service ids applied via setTaxonomy. No id overlap — never merge them.
  // Empty selections omit their key so existing links are left untouched (sending []
  // would clear them). See utils/taxonomy.ts.
  const taxonomy = buildTaxonomyPayload(formData)

  const productData = status === "DRAFT"
    ? {
        name: formData.name,
        description: formData.details,
        categoryId: formData.category,
        ...optionalIdField('sizeGuideId', formData.styleGuide),
        productionTimeInDays: parseInt(formData.productionTimeInDays) || 0,
        ...buildSaleModePayload(formData),
        sizes,
        ...(media.length > 0 ? { media } : {}),
        ...taxonomy,
      }
    : {
        name: formData.name,
        description: formData.details,
        categoryId: formData.category,
        ...optionalIdField('sizeGuideId', formData.styleGuide),
        productionTimeInDays: parseInt(formData.productionTimeInDays) || 0,
        ...buildSaleModePayload(formData),
        status,
        sizes,
        ...(media.length > 0 ? { media } : {}),
        ...taxonomy,
      };

  multipartFormData.append('productData', JSON.stringify(productData))
  
  return multipartFormData
}